import { getUserSettingsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/userKeys.js';
import { isCometMcpSourceType } from '#pplx/frontend/apps/ask/sources/types/cometMcpSourceTypes.js';
import { useUserSettingsQuery } from '#pplx/frontend/apps/ask/user-settings/hooks/useUserSettingsQuery.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
export const useSourceLimits = ({ enabled = true } = {}) => {
    const reason = 'use-source-limits';
    const settings = useUserSettingsQuery({ reason, enabled });
    const queryClient = useQueryClient();
    /**
     * Get the monthly limit and remaining for a source. Returns Infinity if the source has no limit (null)
     */
    const getSourceLimit = useCallback((sourceType) => {
        if (isCometMcpSourceType(sourceType)) {
            return {
                monthlyLimit: Infinity,
                remaining: Infinity,
            };
        }
        if (sourceType in settings.sources.source_to_limit) {
            const { monthly_limit = 0, remaining = 0 } = settings.sources.source_to_limit[sourceType] ?? {};
            return {
                monthlyLimit: monthly_limit ?? Infinity,
                remaining: remaining ?? Infinity,
            };
        }
        return {
            monthlyLimit: 0,
            remaining: 0,
        };
    }, [settings.sources.source_to_limit]);
    /**
     * Check if a source is within its limit
     */
    const getSourceLimited = useCallback((sourceType) => {
        const { remaining = 0 } = getSourceLimit(sourceType);
        return remaining === 0;
    }, [getSourceLimit]);
    /**
     * Check if any source limits exist, and if so, refetch user settings to get the latest limits
     */
    const checkSourceLimits = useCallback(async () => {
        const hasLimits = Object.values(settings.sources.source_to_limit).some(({ monthly_limit }) => monthly_limit !== null);
        if (!hasLimits)
            return;
        // if this hook is not enabled just invalidate the query key
        if (!enabled) {
            queryClient.invalidateQueries({
                queryKey: getUserSettingsQueryKey(),
            });
            return;
        }
        await settings.refetch();
    }, [settings, enabled, queryClient]);
    return useMemo(() => {
        return {
            getSourceLimit,
            getSourceLimited,
            checkSourceLimits,
        };
    }, [getSourceLimit, getSourceLimited, checkSourceLimits]);
};
//# sourceMappingURL=useSourceLimits.js.map